import type { ButtonHTMLAttributes, ReactElement } from 'react';
import React, { forwardRef } from 'react';

import { type BadgeProps, Box, IconButton, styled } from '@mui/material';

import { MuiBadge } from '@shared/ui-kit/badges/MuiBadge/MuiBadge';

import type { IconButtonProps, SxProps, Theme } from '@mui/material';

type IconButtonVariant = 'navigation';
type MuiIconButtonProps = {
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary';
  variant?: IconButtonVariant;
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  isDisabled?: boolean;
  isStopPropagation?: boolean;
  isPrimaryHover?: boolean;
  children?: ReactElement | ReactElement[] | string;
  withBackdropFilter?: boolean;
  disableBackground?: boolean;
  isRounded?: boolean;
  isSelected?: boolean;
  badgeCount?: string | number;
  showUnreadBudge?: boolean;
  changeBadgePosition?: SxProps<Theme>;
  badgeVariant?: BadgeProps['variant'];
  fillColor?: string;
  isHideBoxShadow?: boolean;
} & Partial<IconButtonProps>;

export const MuiIconButton = forwardRef<HTMLButtonElement, MuiIconButtonProps>(
  ({
    size = 'medium',
    color,
    type = 'button',
    isDisabled,
    children,
    isStopPropagation = true,
    isSelected = false,
    isRounded = false,
    showUnreadBudge,
    badgeCount,
    isHideBoxShadow,
    onClick,
    withBackdropFilter,
    disableBackground,
    changeBadgePosition,
    badgeVariant = 'standard',
    fillColor,
    ...args
  }) => {
    const handleClick = (e: React.MouseEvent<any, MouseEvent>) => {
      if (isStopPropagation) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (onClick) {
        onClick(e);
      }
    };

    const invisible = !badgeCount ? false : showUnreadBudge;

    return (
      <Box sx={{ position: 'relative' }}>
        <CustomIconButton
          {...args}
          type={type}
          isHideBoxShadow={isHideBoxShadow}
          withBackdropFilter={withBackdropFilter}
          color={color}
          size={size}
          isSelected={isSelected}
          isRounded={isRounded}
          disabled={isDisabled}
          onClick={handleClick}
          disableBackground={disableBackground}
          fillColor={fillColor}
        >
          {children}
        </CustomIconButton>

        <MuiBadge
          sx={{
            top: showUnreadBudge ? '-13px' : '-15px',
            right: '4px',
            ...changeBadgePosition,
          }}
          invisible={invisible}
          variant={showUnreadBudge ? 'dot' : badgeVariant}
          color="error"
          badgeContent={badgeCount ?? null}
          size="small"
        />
      </Box>
    );
  },
);

type StyledIconButtonProps = {
  isRounded: boolean;
  isSelected: boolean;
  variant?: IconButtonVariant;
  withBackdropFilter?: boolean;
  disableBackground?: boolean;
  isHideBoxShadow?: boolean;
  fillColor?: string;
};

const CustomIconButton = styled(IconButton, {
  shouldForwardProp: (prop: string) =>
    ![
      'isRounded',
      'isSelected',
      'variant',
      'withBackdropFilter',
      'disableBackground',
      'fillColor',
      'isHideBoxShadow',
    ].includes(prop),
})<StyledIconButtonProps>(
  ({
    theme,
    isRounded,
    isSelected,
    variant,
    withBackdropFilter,
    disableBackground,
    fillColor,
    isHideBoxShadow,
  }) => ({
    '&.MuiIconButton-root': {
      borderRadius: isRounded ? '8px' : '50%',
      svg: { path: { fill: fillColor || theme.palette.case.neutral.n500 } },

      '&:hover': {
        backgroundColor: theme.palette.case.neutral.n200,
        svg: {
          path: { fill: theme.palette.case.neutral.n700 },
        },
      },
      '&:active': {
        backgroundColor: theme.palette.case.neutral.n200,
        svg: {
          path: { fill: theme.palette.case.neutral.n800 },
        },
      },
      '&: disabled': {
        backgroundColor: theme.palette.case.neutral.n100,
        svg: { path: { fill: theme.palette.case.neutral.n400 } },
      },

      ...(variant === 'navigation' && {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        backgroundColor: theme.palette.case.neutral.n0,
        border: `1px solid ${theme.palette.case.neutral.n200}`,
        boxShadow: isHideBoxShadow ? '' : theme.palette.case.shadow.default,
        svg: {
          width: '24px',
          height: '24px',
          path: { fill: theme.palette.case.neutral.n500 },
        },
        '&:hover': { svg: { path: { fill: theme.palette.case.neutral.n700 } } },
        '&:active': { svg: { path: { fill: theme.palette.case.neutral.n800 } } },
        '&: disabled': {
          svg: { path: { fill: theme.palette.case.neutral.n400 } },
          backgroundColor: theme.palette.case.neutral.n75,
          boxShadow: 'none',
        },

        '&.MuiIconButton-sizeSmall': {
          width: '24px',
          height: '24px',
          svg: { width: '16px', height: '16px', path: { fill: theme.palette.case.neutral.n500 } },
          '&:hover': { svg: { path: { fill: theme.palette.case.neutral.n700 } } },
          '&:active': { svg: { path: { fill: theme.palette.case.neutral.n800 } } },
          '&: disabled': {
            svg: { path: { fill: theme.palette.case.neutral.n400 } },
            backgroundColor: theme.palette.case.neutral.n75,
            boxShadow: 'none',
          },
        },
      }),
      ...(withBackdropFilter && {
        svg: { path: { fill: theme.palette.case.neutral.n700 } },
        backdropFilter: 'blur(2px)',
        background: theme.palette.case.backdrop.light,
        [theme.breakpoints.down('sm')]: {
          background: theme.palette.case.neutral.n200,
        },
      }),
      ...(disableBackground && {
        '&.MuiIconButton-root': {
          background: 'none',
        },
      }),

      ...(isSelected && {
        backgroundColor: theme.palette.case.neutral.n200,
        svg: { path: { fill: theme.palette.case.neutral.n800 } },

        '&:hover': {
          backgroundColor: theme.palette.case.neutral.n200,
          svg: { path: { fill: theme.palette.case.neutral.n800 } },
        },
        '&:active': {
          backgroundColor: theme.palette.case.neutral.n200,
          svg: { path: { fill: theme.palette.case.neutral.n800 } },
        },
      }),
    },
  }),
);
